import React from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Sparkles, Megaphone, MapPin, Palette, Users, BarChart3,
  Radio, Handshake, Settings, Wrench, Tag, Target, Zap, Gift, Clapperboard,
  Inbox, Building2, Briefcase, UserCog, ScrollText, Users2, ListChecks, Grid3x3, ShieldCheck, Monitor, Store,
} from "lucide-react";
import { auth } from "../../lib/sr";

const D = {
  home: { to: "/app", label: "Home", icon: LayoutDashboard, desc: "Your workspace overview" },
  results: { to: "/app/results", label: "Performance", icon: BarChart3, desc: "Results & ROI" },
  clients: { to: "/app/admin/clients", label: "Clients", icon: Building2, desc: "All client businesses" },
  agency: { to: "/app/agency", label: "Agency", icon: Briefcase, desc: "Agency portfolio" },
  customers: { to: "/app/customers", label: "Customers", icon: Users, desc: "Customer list" },
  growth: { to: "/app/growth-plan", label: "Growth Plan", icon: Sparkles, desc: "AI growth plans" },
  autopilot: { to: "/app/autopilot", label: "Autopilot", icon: Zap, desc: "Automated growth" },
  campaigns: { to: "/app/campaigns", label: "Campaigns", icon: Megaphone, desc: "Live & draft campaigns" },
  locations: { to: "/app/locations", label: "Locations", icon: MapPin, desc: "Stores & areas" },
  competitors: { to: "/app/competitors", label: "Competitor Studio", icon: Target, desc: "Competitor intel" },
  leads: { to: "/app/leads", label: "Lead Concierge", icon: Inbox, desc: "Inbound leads" },
  radio: { to: "/app/radio", label: "Radio Sync", icon: Radio, desc: "Radio flights" },
  creative: { to: "/app/creative", label: "Creative", icon: Palette, desc: "Ad creative" },
  video: { to: "/app/video", label: "Video Studio", icon: Clapperboard, desc: "Video ads" },
  offers: { to: "/app/offers", label: "Offer Studio", icon: Tag, desc: "Coupons & offers" },
  loyalty: { to: "/app/loyalty", label: "Loyalty Loop", icon: Gift, desc: "Loyalty program" },
  franchise: { to: "/app/franchise", label: "Franchise", icon: Building2, desc: "Multi-store" },
  queue: { to: "/app/admin/queue", label: "Assisted Queue", icon: Zap, desc: "Clients needing attention" },
  work: { to: "/app/work", label: "Work Queue", icon: ListChecks, desc: "Claim tasks · what's next" },
  myaccess: { to: "/app/my-access", label: "My Access", icon: ShieldCheck, desc: "What you can do here" },
  opsDigital: { to: "/app/ops/digital", label: "Digital Ops", icon: Monitor, desc: "Digital delivery workstream" },
  opsRadio: { to: "/app/ops/radio", label: "Radio Ops", icon: Radio, desc: "Radio delivery workstream" },
  radioAudiences: { to: "/app/admin/radio-audiences", label: "Station Audiences", icon: Radio, desc: "Enter station audiences to turn on reach" },
  portal: { to: "/app/portal", label: "Client Portal", icon: Store, desc: "Your approvals & campaigns" },
  overdue: { to: "/app/admin/overdue", label: "Overdue Approvals", icon: ListChecks, desc: "Stuck sign-offs · remind" },
  importClient: { to: "/app/admin/import", label: "Import Client", icon: Building2, desc: "Set up from Google Places" },
  workload: { to: "/app/admin/workload", label: "Team & Capacity", icon: Users2, desc: "Manager workload" },
  users: { to: "/app/admin/users", label: "Users", icon: UserCog, desc: "Accounts & roles" },
  roles: { to: "/app/admin/roles", label: "Roles & Permissions", icon: ShieldCheck, desc: "Role templates & scoped access" },
  activity: { to: "/app/admin/activity", label: "Activity", icon: ScrollText, desc: "Admin audit log" },
  services: { to: "/app/services", label: "Services", icon: Handshake, desc: "Service packages" },
  settings: { to: "/app/settings", label: "Settings", icon: Settings, desc: "Business settings" },
  ops: { to: "/ops", label: "Operations", icon: Wrench, desc: "Ops console" },
  opsTasks: { to: "/app/admin/ops-tasks", label: "Ops Task Inbox", icon: Inbox, desc: "Partner intros & setup requests" },
};

const BASE = [
  { name: "Projects & Campaigns", items: ["growth", "autopilot", "campaigns", "work"] },
  { name: "Digital Operations", items: ["opsDigital", "locations", "competitors", "leads"] },
  { name: "Radio Operations", items: ["opsRadio", "radio", "radioAudiences"] },
  { name: "Creative & Offers", items: ["creative", "video", "offers", "loyalty"] },
  { name: "Clients & Franchise", items: ["clients", "agency", "customers", "franchise", "portal"] },
  { name: "Measurement", items: ["results"] },
  { name: "Account", items: ["myaccess", "settings"] },
];
const CLIENT = [
  { name: "Portal", items: ["portal"] },
  { name: "Projects & Campaigns", items: ["campaigns"] },
  { name: "Creative & Offers", items: ["creative", "offers", "loyalty"] },
  { name: "Measurement", items: ["results"] },
  { name: "Account", items: ["myaccess", "settings"] },
];

export default function MoreTools() {
  const user = auth.getUser();
  const isStaff = user && ["admin", "super_admin", "campaign_manager", "compliance_reviewer"].includes(user.role);
  const isAdmin = user && ["admin", "super_admin"].includes(user.role);
  const groups = !isStaff ? CLIENT
    : [...BASE, ...(isAdmin ? [{ name: "Administration", items: ["importClient", "queue", "opsTasks", "workload", "users", "roles", "overdue", "activity"] }] : []),
       ...(isStaff ? [{ name: "Operations", items: ["ops"] }] : [])];

  return (
    <div data-testid="more-tools-page">
      <div style={{ marginBottom: 16 }}>
        <span className="sr-badge" style={{ marginBottom: 8 }}><Grid3x3 size={12} /> All tools</span>
        <h1 style={{ fontSize: 30 }}>More</h1>
        <p className="sr-muted" style={{ marginTop: 6 }}>Every tool available to you. Switch to Pro mode anytime to keep these in the sidebar.</p>
      </div>
      {groups.map((g) => (
        <div key={g.name} style={{ marginBottom: 22 }}>
          <h3 style={{ fontSize: 15, color: "var(--navy)", marginBottom: 10 }}>{g.name}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
            {g.items.map((k) => {
              const d = D[k]; if (!d) return null;
              return (
                <Link key={k} to={d.to} data-testid={`more-${k}`} className="sr-card"
                  style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none", padding: 16 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--blue-light)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <d.icon size={19} style={{ color: "var(--blue)" }} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 14 }}>{d.label}</div>
                    <div className="sr-muted" style={{ fontSize: 12 }}>{d.desc}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
